import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/shared/lib/utils";
import {
  type LucideIcon,
  FileText,
  ClipboardCheck,
  AlertTriangle,
  FileBarChart,
  BarChart3,
  Users,
  FileWarning,
  Layers,
  LayoutDashboard,
  ChevronLeft,
  ChevronDown,
  Building2,
  UserCog,
  Zap,
  Brain,
  Network,
  MessagesSquare,
  Package,
  User,
  LogOut,
  Settings,
  Trash2,
} from "lucide-react";
import { useAuth } from "@/core/AuthContext";
import { useFactoryStore } from "@/core/hooks/useFactory";
import { useHasPermission } from "@/core/hooks/usePermissions";
import { useAppScope } from "@/core/AppScopeContext";
import { useLanguage } from "@/core/LanguageContext";
import { useUserTenants, useAllTenants } from "@/core/hooks/useTenant";
import { LanguageSelector } from "@/core/components/LanguageSelector";
import { TenantSelector } from "@/core/components/TenantSelector";
import { useTheme } from "next-themes";
import fpBannerColor from "@/assets/fp-banner-color.png";
import fpBannerWhite from "@/assets/fp-banner-white.png";
import fpIconColor from "@/assets/fp-icon-color.png";
import fpIconWhite from "@/assets/fp-icon-white.png";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/toggle-group";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/shared/ui/sidebar";
import { Moon, Sun } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/avatar";
import { useNavigate } from "react-router-dom";

type NavItem = {
  key: string;
  title: string;
  url: string;
  icon: LucideIcon;
  operatorVisible: boolean;
  requiresFactory?: boolean;
  requiresAdmin?: boolean;
};

type NavSection = {
  key: string;
  title: string;
  items: NavItem[];
};

const dashboardItems: NavItem[] = [
  {
    key: "dashboard",
    title: "Dashboard",
    url: "/",
    icon: LayoutDashboard,
    operatorVisible: true,
  },
  {
    key: "factoryDashboard",
    title: "Factory Dashboard",
    url: "/factory-dashboard",
    icon: LayoutDashboard,
    operatorVisible: false,
    requiresFactory: true,
  },
  {
    key: "aiControl",
    title: "AI Production Control",
    url: "/ai-control",
    icon: Brain,
    operatorVisible: false,
  },
];

const navigationSections: NavSection[] = [
  {
    key: "supplierManagement",
    title: "Supplier Management",
    items: [
      {
        key: "eightD",
        title: "Complaints",
        url: "/8d-management",
        icon: FileWarning,
        operatorVisible: false,
      },
      {
        key: "supplierQuality",
        title: "Suppliers",
        url: "/supplier-quality",
        icon: Package,
        operatorVisible: false,
      },
    ],
  },
  {
    key: "customerManagement",
    title: "Customer Management",
    items: [
      {
        key: "customers",
        title: "Customers",
        url: "/customers",
        icon: Building2,
        operatorVisible: false,
      },
      {
        key: "customerComplaints",
        title: "Complaints",
        url: "/customer-complaints",
        icon: FileWarning,
        operatorVisible: false,
      },
    ],
  },
  {
    key: "people",
    title: "People",
    items: [
      {
        key: "userManagement",
        title: "User Management",
        url: "/admin/users",
        icon: UserCog,
        operatorVisible: false,
        requiresAdmin: true,
      },
      {
        key: "competency",
        title: "Competency",
        url: "/competency",
        icon: Users,
        operatorVisible: true,
      },
    ],
  },
  // {
  //   key: "toBeRemoved",
  //   title: "To Be Removed",
  //   items: [
  //     {
  //       key: "qualityChecks",
  //       title: "Quality Checks",
  //       url: "/quality-checks",
  //       icon: ClipboardCheck,
  //       operatorVisible: true,
  //     },
  //     {
  //       key: "defectTagging",
  //       title: "Defect Tagging",
  //       url: "/defects",
  //       icon: AlertTriangle,
  //       operatorVisible: true,
  //     },
  //     {
  //       key: "processAudits",
  //       title: "Process Audits",
  //       url: "/audits",
  //       icon: Layers,
  //       operatorVisible: false,
  //     },
  //   ],
  // },
  {
    key: "quality",
    title: "Quality",
    items: [
      {
        key: "lpa",
        title: "LPA Audits",
        url: "/lpa",
        icon: ClipboardCheck,
        operatorVisible: false,
        requiresFactory: true,
      },
    ],
  },
  {
    key: "efficiency",
    title: "Efficiency",
    items: [
      {
        key: "performance",
        title: "Performance",
        url: "/performance",
        icon: BarChart3,
        operatorVisible: false,
      },
      {
        key: "scrapEntry",
        title: "Scrap Entry",
        url: "/scrap-entry",
        icon: Trash2,
        operatorVisible: true,
      },
      {
        key: "energy",
        title: "Energy Management",
        url: "/energy",
        icon: Zap,
        operatorVisible: false,
      },
    ],
  },
  {
    key: "other",
    title: "Other",
    items: [
      {
        key: "workInstructions",
        title: "Work Instructions",
        url: "/instructions",
        icon: FileText,
        operatorVisible: true,
      },
      {
        key: "shiftReports",
        title: "Shift Reports",
        url: "/shift-reports",
        icon: FileBarChart,
        operatorVisible: false,
      },
      {
        key: "documentAssistant",
        title: "Document Assistant",
        url: "/document-assistant",
        icon: MessagesSquare,
        operatorVisible: false,
      },
      {
        key: "factoryStructure",
        title: "Factory Structure",
        url: "/admin/factory-structure",
        icon: Building2,
        operatorVisible: false,
        requiresAdmin: true,
      },
      {
        key: "unsMapper",
        title: "UNS Mapper",
        url: "/admin/uns-mapper",
        icon: Network,
        operatorVisible: false,
        requiresAdmin: true,
      },
    ],
  },
];

export function AppSidebar() {
  const location = useLocation();
  const { resolvedTheme, setTheme } = useTheme();
  const { user, profile, signOut } = useAuth();
  const { selectedFactoryId } = useFactoryStore();
  const { data: memberships = [] } = useUserTenants();
  const isAdmin = useHasPermission("factory.admin");
  const showAdmin = profile?.is_super_admin || isAdmin;
  const { effectiveMode } = useAppScope();
  const { t, availableLanguages } = useLanguage();
  const { state, toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const isCollapsed = state === "collapsed";
  const showLanguageSelector = availableLanguages.length > 1;
  const isDark = resolvedTheme === "dark";
  const logoSrc = isDark ? fpBannerWhite : fpBannerColor;
  const iconSrc = isDark ? fpIconWhite : fpIconColor;
  const initials = (profile?.full_name || user?.email || "U")
    .split(" ")
    .map((namePart) => namePart[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const { data: allTenants = [] } = useAllTenants(!!profile?.is_super_admin);

  const approvedMemberships = memberships.filter((membership) => membership.status === "approved");
  const tenantCount = profile?.is_super_admin ? allTenants.length : approvedMemberships.length;
  const showTenantSelector = tenantCount > 1;

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };


  const canShowAdminItems = showAdmin && effectiveMode === "supervisor";

  const filterNavItems = (items: NavItem[]) =>
    items.filter((item) => {
      if (effectiveMode === "operator" && !item.operatorVisible) {
        return false;
      }

      if (item.requiresFactory && !selectedFactoryId) {
        return false;
      }

      if (item.requiresAdmin && !canShowAdminItems) {
        return false;
      }

      return true;
    });

  const visibleDashboardItems = filterNavItems(dashboardItems);
  const visibleSections = navigationSections
    .map((section) => ({ ...section, items: filterNavItems(section.items) }))
    .filter((section) => section.items.length > 0);
  const showSectionSeparator = visibleDashboardItems.length > 0 && visibleSections.length > 0;

  const renderMenuItems = (items: NavItem[]) => (
    <SidebarMenu className="gap-1">
      {items.map((item) => {
        const isActive = location.pathname === item.url;
        return (
          <SidebarMenuItem key={item.url}>
            <SidebarMenuButton
              asChild
              size="default"
              isActive={isActive}
              tooltip={t(`nav.${item.key}`, item.title)}
            >
              <NavLink
                to={item.url}
                className={cn(
                  isActive && "bg-sidebar-primary/10 text-sidebar-primary border-l-2 border-sidebar-primary"
                )}
              >
                <item.icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-sidebar-primary")} />
                <span>{t(`nav.${item.key}`, item.title)}</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  return (
    <Sidebar collapsible="icon">
      {/* Logo with increased vertical padding */}
      <SidebarHeader className="py-4 px-3 border-b border-sidebar-border">
        <div className="flex items-center justify-center">
          {isCollapsed ? (
            <img
              src={iconSrc}
              alt="Factory Pilot"
              className="h-8 w-8 object-contain"
            />
          ) : (
            <img
              src={logoSrc}
              alt="Factory Pilot"
              className="h-8 w-auto max-w-[180px] object-contain"
            />
          )}
        </div>
      </SidebarHeader>

      {/* Scrollable navigation content */}
      <SidebarContent className="gap-1.5 sidebar-scrollbar">
        {visibleDashboardItems.length > 0 && (
          <SidebarGroup className="px-2 py-1.5">
            <SidebarGroupLabel className="h-8 px-2 text-base font-semibold">
              {t("nav.group.dashboards", "Dashboards")}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              {renderMenuItems(visibleDashboardItems)}
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {showSectionSeparator && <SidebarSeparator />}

        {visibleSections.map((section) => (
          <Collapsible key={section.key} defaultOpen className="group/collapsible">
            <SidebarGroup className="px-2 py-1.5">
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger className="flex w-full items-center gap-2 cursor-pointer h-8 px-2 text-base font-semibold">
                  <span>{t(`nav.group.${section.key}`, section.title)}</span>
                  <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>{renderMenuItems(section.items)}</SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        ))}
      </SidebarContent>

      {/* Anchored footer with collapse and user menu */}
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={toggleSidebar}
              tooltip={isCollapsed ? "Expand" : "Collapse"}
            >
              <ChevronLeft
                className={cn(
                  "w-4 h-4 transition-transform",
                  isCollapsed && "rotate-180"
                )}
              />
              <span>{t("nav.collapse", "Collapse")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="md"
                  className={cn("gap-3", isCollapsed && "justify-center gap-0")}
                  tooltip={isCollapsed ? profile?.full_name || t("user.nameFallback", "User") : undefined}
                >
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className={cn("flex min-w-0 flex-col text-left leading-tight", isCollapsed && "hidden")}>
                    <span className="text-sm font-medium truncate">
                      {profile?.full_name || t("user.nameFallback", "User")}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-60 bg-popover">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={profile?.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-col">
                      <span className="text-sm font-medium truncate">
                        {profile?.full_name || t("user.nameFallback", "User")}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {showTenantSelector && (
                  <>
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                      {t("user.tenant", "Tenant")}
                    </DropdownMenuLabel>
                    <div className="px-2 py-1.5">
                      <TenantSelector onlyWhenMultiple />
                    </div>
                    <DropdownMenuSeparator />
                  </>
                )}
                {showLanguageSelector && (
                  <>
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                      {t("user.language", "Language")}
                    </DropdownMenuLabel>
                    <div className="px-2 py-1.5">
                      <LanguageSelector triggerClassName="w-full" />
                    </div>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                  {t("user.theme", "Theme")}
                </DropdownMenuLabel>
                <div className="px-2 py-1.5">
                  <ToggleGroup
                    type="single"
                    value={isDark ? "dark" : "light"}
                    onValueChange={(value) => value && setTheme(value)}
                    className="w-full grid grid-cols-2 gap-1 rounded-md border border-border bg-background p-1"
                  >
                    <ToggleGroupItem value="light" className="w-full text-xs gap-2">
                      <Sun className="h-3.5 w-3.5" />
                      {t("user.themeLight", "Light")}
                    </ToggleGroupItem>
                    <ToggleGroupItem value="dark" className="w-full text-xs gap-2">
                      <Moon className="h-3.5 w-3.5" />
                      {t("user.themeDark", "Dark")}
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  {t("user.profileSettings", "Profile Settings")}
                </DropdownMenuItem>
                {showAdmin && (
                  <>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/factory-structure")}
                      className="cursor-pointer"
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      {t("user.factoryStructure", "Factory Structure")}
                    </DropdownMenuItem>
                    {profile?.is_super_admin && (
                      <DropdownMenuItem onClick={() => navigate("/superadmin")} className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        {t("user.adminSettings", "Admin Settings")}
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("user.signOut", "Sign Out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
